import {
  createSlice,
  type PayloadAction,
  type ActionReducerMapBuilder,
} from '@reduxjs/toolkit';
import {
  UserStateEnum
} from '@shared/types';
import {
  type HowItWorksBlockType
} from '@widgets/how-it-works-block';
import {
  type Referral
} from '../lib';
import {
  referralPageSlice,
  referralLinksSlice,
  type GetRefereesResponse,
  type GetReferralLinksResponse,
} from '../api';

export type ActionItem = {
  text: string;
  url: Url;
  userState: UserStateEnum;
};

export type ReferAFriendPageCarouselSliderItem = {
  id: number;
  actions: Array<ActionItem>;
  variant1ImgSrc: Url;
  variant2ImgSrc: Url;
  bannerUrl: Url;
  isVariant1ItemLooped: boolean;
  isVariant2ItemLooped: boolean;
};

export type ReferAFriendPageUserCarouselSliderItem = Prettify<Omit<ReferAFriendPageCarouselSliderItem, 'actions'> & {
  action: Maybe<ActionItem>;
}>;

export type ReferAFriendPageState = {
  title: Maybe<string>;
  description: Maybe<string>;
  keywords: Maybe<string>;
  canonical: Maybe<string>;
  slider: Maybe<Array<ReferAFriendPageUserCarouselSliderItem>>;
  howItWorksBlock?: Maybe<HowItWorksBlockType>;
  referralLinkFieldTitle: Maybe<string>;
  socialShareBarTitle: Maybe<string>;
  facebookShareText: Maybe<string>;
  twitterShareText: Maybe<string>;
  whatsappShareText: Maybe<string>;
  emailShareSubject: Maybe<string>;
  emailShareBody: Maybe<string>;
  referralCode: Maybe<string>;
  referees: Maybe<Array<Referral>>;
};

const initialState: ReferAFriendPageState = {
  title: null,
  description: null,
  keywords: null,
  canonical: null,
  slider: null,
  referralLinkFieldTitle: null,
  socialShareBarTitle: null,
  facebookShareText: null,
  twitterShareText: null,
  whatsappShareText: null,
  emailShareSubject: null,
  emailShareBody: null,
  referralCode: null,
  referees: null
};

export const referAFriendPageSlice = createSlice({
  name: 'refer-a-friend-page',
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<ReferAFriendPageState>) => {
    builder.addMatcher(
      referralPageSlice.endpoints.getReferAFriendPage.matchFulfilled,
      (state: ReferAFriendPageState, action: PayloadAction<ReferAFriendPageState>) => {
        state.title = action.payload.title;
        state.description = action.payload.description;

        if (action?.payload.keywords) state.keywords = action.payload.keywords;
        if (action?.payload.canonical) state.canonical = action.payload.canonical;

        state.slider = action.payload.slider;
        state.referralLinkFieldTitle = action.payload.referralLinkFieldTitle;
        state.socialShareBarTitle = action.payload.socialShareBarTitle;

        if (action?.payload.facebookShareText) state.facebookShareText = action.payload.facebookShareText;
        if (action?.payload.twitterShareText) state.twitterShareText = action.payload.twitterShareText;
        if (action?.payload.whatsappShareText) state.whatsappShareText = action.payload.whatsappShareText;
        if (action?.payload.howItWorksBlock) state.howItWorksBlock = action.payload.howItWorksBlock;

        state.emailShareSubject = action.payload.emailShareSubject;
        state.emailShareBody = action.payload.emailShareBody;
      }
    );
    builder.addMatcher(
      referralPageSlice.endpoints.getReferees.matchFulfilled,
      (state: ReferAFriendPageState, action: PayloadAction<GetRefereesResponse>) => {
        state.referees = action.payload.referees;
      }
    );
    builder.addMatcher(
      referralLinksSlice.endpoints.getReferralLinks.matchFulfilled,
      (state: ReferAFriendPageState, action: PayloadAction<GetReferralLinksResponse>) => {
        const referralLinks = action.payload.referralLinks;
        if (referralLinks) state.referralCode = referralLinks[0]?.referralCode || null;
      }
    );
  },
});

export const referAFriendPageReducer = referAFriendPageSlice.reducer;
